import type { ReactNode } from 'react';
import React from 'react';

import { useIntl } from 'react-intl-next';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { clearFormatting, getAriaKeyshortcuts, tooltip } from '@atlaskit/editor-common/keymaps';
import { toolbarMessages } from '@atlaskit/editor-common/messages';
import {
	ToolbarButton,
	ToolbarDropdownItem,
	ClearFormattingIcon,
	ToolbarKeyboardShortcutHint,
	type ToolbarButtonGroupLocation,
	ToolbarDropdownMenu,
	MoreItemsIcon,
	ToolbarTooltip,
} from '@atlaskit/editor-toolbar';

import { clearFormattingWithAnalyticsNext } from '../../../editor-commands/clear-formatting';

import type { FormatComponentProps } from './utils';
import { getInputMethodFromParentKeys, useComponentInfo } from './utils';

export const FormatMenuItem = ({
	parents,
	api,
	optionType,
	toggleMarkWithAnalyticsCallback,
	icon,
	shortcut,
	title,
}: FormatComponentProps) => {
	const { isActive, isDisabled, isHidden, shortcutContent, onClick, ariaLabel, formatTitle } =
		useComponentInfo({
			api,
			optionType,
			title,
			shortcut,
			toggleMarkWithAnalyticsCallback,
			parents,
		});
	const Icon = icon;

	if (isHidden) {
		return null;
	}

	return (
		<ToolbarDropdownItem
			elemBefore={<Icon label="" />}
			elemAfter={shortcutContent && <ToolbarKeyboardShortcutHint shortcut={shortcutContent} />}
			isDisabled={isDisabled}
			isSelected={isActive}
			onClick={onClick}
			aria-keyshortcuts={getAriaKeyshortcuts(shortcut)}
			aria-label={ariaLabel}
		>
			{formatTitle}
		</ToolbarDropdownItem>
	);
};

export const FormatButton = ({
	parents,
	api,
	optionType,
	toggleMarkWithAnalyticsCallback,
	icon,
	shortcut,
	title,
	groupLocation = 'start',
}: FormatComponentProps) => {
	const { isActive, isDisabled, onClick, ariaLabel, formatTitle } = useComponentInfo({
		api,
		optionType,
		title,
		shortcut,
		toggleMarkWithAnalyticsCallback,
		parents,
	});

	const Icon = icon;
	return (
		// TODO: ED-28743 - add keyboard shortcut here
		<ToolbarTooltip content={formatTitle}>
			<ToolbarButton
				iconBefore={<Icon label={ariaLabel} />}
				onClick={onClick}
				isSelected={isActive}
				isDisabled={isDisabled}
				groupLocation={groupLocation}
				ariaKeyshortcuts={getAriaKeyshortcuts(shortcut)}
			/>
		</ToolbarTooltip>
	);
};

export const ClearFormatMenuItem = ({
	api,
	parents,
}: Pick<FormatComponentProps, 'api' | 'parents'>) => {
	const { isInitialised, isFormattingPresent } = useSharedPluginStateWithSelector(
		api,
		['textFormatting'],
		(states) => ({
			isInitialised: states.textFormattingState?.isInitialised,
			isFormattingPresent: states.textFormattingState?.formattingIsPresent,
		}),
	);
	const { formatMessage } = useIntl();

	if (!isInitialised) {
		return null;
	}

	const formatTitle = formatMessage(toolbarMessages.clearFormatting);
	const shortcutContent = tooltip(clearFormatting);
	const ariaLabel = tooltip(clearFormatting, formatTitle);
	const onClick = () => {
		api?.core.actions.execute(
			clearFormattingWithAnalyticsNext(api?.analytics?.actions)(
				getInputMethodFromParentKeys(parents),
			),
		);
	};

	return (
		<ToolbarDropdownItem
			elemBefore={<ClearFormattingIcon label="" />}
			elemAfter={shortcutContent && <ToolbarKeyboardShortcutHint shortcut={shortcutContent} />}
			isDisabled={!isFormattingPresent}
			onClick={onClick}
			aria-keyshortcuts={getAriaKeyshortcuts(clearFormatting)}
			aria-label={ariaLabel}
		>
			{formatTitle}
		</ToolbarDropdownItem>
	);
};

export const MoreFormattingMenu = ({
	children,
	groupLocation,
}: {
	children?: ReactNode;
	groupLocation?: ToolbarButtonGroupLocation;
}) => {
	const { formatMessage } = useIntl();
	const content = formatMessage(toolbarMessages.moreFormatting);
	return (
		<ToolbarDropdownMenu
			iconBefore={<MoreItemsIcon label={content} testId="more-formatting" />}
			groupLocation={groupLocation}
		>
			{children}
		</ToolbarDropdownMenu>
	);
};
